import React, { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, Upload, X, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { gameService } from '../../services/gameService';
import { sportStatsService } from '../../services/sportStatsService';
import { parseHudlCsv, aggregateHudlStats, deriveScoreFromPlays, type HudlPlay, type HudlPlayerRef } from '../../services/hudlImport';
import type { GamePlay, Player } from '../../types';

interface HudlImportModalProps {
  gameId: string;
  teamId: string;
  players: Player[];
  /** player_id → player_card_id (needed to save to sport_stats) */
  playerCards: Map<string, string>;
  hasExistingStats: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'select' | 'preview' | 'importing';

interface MatchRow {
  ref: HudlPlayerRef;
  matchedPlayerId: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function autoMatch(ref: HudlPlayerRef, players: Player[]): string | null {
  // 1. Match by jersey number (most reliable)
  if (ref.jerseyNumber) {
    const byNum = players.find(
      (p) => p.number != null && String(p.number).trim() === ref.jerseyNumber,
    );
    if (byNum) return byNum.id;
  }
  // 2. Match by last name (case-insensitive)
  const nameLower = ref.name.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
  const byLast = players.find((p) => {
    const last = (p.last_name ?? '').toLowerCase();
    return nameLower.includes(last) && last.length > 1;
  });
  if (byLast) return byLast.id;
  return null;
}

function playerLabel(p: Player) {
  const num = p.number != null ? `#${p.number} ` : '';
  return `${num}${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
}

// ── Component ─────────────────────────────────────────────────────────────────

export const HudlImportModal: React.FC<HudlImportModalProps> = ({
  gameId,
  teamId,
  players,
  playerCards,
  hasExistingStats,
  onClose,
  onImported,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('select');
  const [plays, setPlays] = useState<HudlPlay[]>([]);
  const [matchRows, setMatchRows] = useState<MatchRow[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [importStatus, setImportStatus] = useState<string>('');

  // ── File selection ──────────────────────────────────────────────────────────

  const handleFile = async (file: File) => {
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = parseHudlCsv(text);

      if (result.plays.length === 0) {
        toast.error('No play rows found — make sure this is a Hudl play-by-play export.');
        return;
      }

      // Auto-match all unique players
      const rows: MatchRow[] = result.uniquePlayers.map((ref) => ({
        ref,
        matchedPlayerId: autoMatch(ref, players),
      }));

      setPlays(result.plays);
      setMatchRows(rows);
      setParseWarnings(result.warnings);
      setStep('preview');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to read file.');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  // ── Player match override ───────────────────────────────────────────────────

  const setMatch = (raw: string, playerId: string | null) => {
    setMatchRows((prev) =>
      prev.map((r) => (r.ref.raw === raw ? { ...r, matchedPlayerId: playerId } : r)),
    );
  };

  // ── Import ──────────────────────────────────────────────────────────────────

  const handleImport = async () => {
    setStep('importing');
    try {
      // Build raw → player_id map from user's match selections
      const idMap = new Map<string, string>();
      for (const row of matchRows) {
        if (row.matchedPlayerId) idMap.set(row.ref.raw, row.matchedPlayerId);
      }

      // Load game details for date/opponent/season metadata
      setImportStatus('Loading game info…');
      let gameDate = new Date().toISOString().split('T')[0];
      let opponent: string | undefined;
      let season: string | undefined;
      try {
        const game = await gameService.getGame(gameId);
        if (game) {
          gameDate = game.date;
          opponent = game.opponent_name ?? undefined;
          season = game.season ?? undefined;
        }
      } catch { /* non-fatal */ }

      // Delete existing plays and stats for this game
      setImportStatus('Clearing existing data…');
      await supabase.from('game_plays').delete().eq('game_id', gameId);
      await sportStatsService.deleteStatsByGameId(gameId);

      // Insert plays into game_plays
      setImportStatus(`Saving ${plays.length} plays…`);
      const playRows: Array<Omit<GamePlay, 'id' | 'created_at'>> = plays.map((p) => ({
        game_id:        gameId,
        team_id:        teamId,
        play_number:    p.playNumber,
        quarter:        p.quarter,
        down:           p.down,
        distance:       p.distance,
        yard_line:      p.yardLine,
        hash:           p.hash,
        odk:            p.odk,
        personnel:      p.personnel,
        formation:      p.formation,
        play_type:      p.playType,
        result:         p.result,
        yards:          p.yards,
        notes:          p.notes,
        passer_id:      idMap.get(p.passerRaw    ?? '') ?? null,
        rusher_id:      idMap.get(p.rusherRaw    ?? '') ?? null,
        receiver_id:    idMap.get(p.receiverRaw  ?? '') ?? null,
        tackler1_id:    idMap.get(p.tackler1Raw  ?? '') ?? null,
        tackler2_id:    idMap.get(p.tackler2Raw  ?? '') ?? null,
        returner_id:    idMap.get(p.returnerRaw  ?? '') ?? null,
        key_player_id:  idMap.get(p.keyPlayerRaw ?? '') ?? null,
        passer_raw:     p.passerRaw,
        rusher_raw:     p.rusherRaw,
        receiver_raw:   p.receiverRaw,
        tackler1_raw:   p.tackler1Raw,
        tackler2_raw:   p.tackler2Raw,
        returner_raw:   p.returnerRaw,
        key_player_raw: p.keyPlayerRaw,
      }));

      // Insert in batches of 200
      const BATCH = 200;
      for (let i = 0; i < playRows.length; i += BATCH) {
        const { error } = await supabase.from('game_plays').insert(playRows.slice(i, i + BATCH));
        if (error) throw error;
      }

      // Aggregate stats per player
      setImportStatus('Aggregating player stats…');
      const statsByPlayer = aggregateHudlStats(plays, idMap);

      // Save to sport_stats
      let savedPlayerCount = 0;
      for (const [playerId, statMap] of statsByPlayer) {
        const cardId = playerCards.get(playerId);
        if (!cardId) continue;
        const saves = Object.entries(statMap)
          .filter(([, v]) => v > 0)
          .map(([key, val]) =>
            sportStatsService.addStat(cardId, teamId, key, val, gameDate, gameId, opponent, season),
          );
        await Promise.all(saves);
        savedPlayerCount++;
      }

      // Derive and save both scores from play results
      setImportStatus('Calculating score…');
      try {
        const { ourScore, opponentScore } = deriveScoreFromPlays(plays);
        await gameService.saveScore(gameId, ourScore, opponentScore);
      } catch { /* non-fatal — score can be set manually */ }

      const matched = matchRows.filter((r) => r.matchedPlayerId).length;
      const unmatched = matchRows.length - matched;
      const msg = unmatched > 0
        ? `Imported ${plays.length} plays, stats for ${savedPlayerCount} players. ${unmatched} player(s) unmatched — edit manually.`
        : `Imported ${plays.length} plays, stats for ${savedPlayerCount} players.`;
      toast.success(msg, { duration: 6000 });
      onImported();
    } catch (err: any) {
      console.error('[HudlImportModal] import error:', err);
      toast.error(err?.message ?? 'Import failed.');
      setStep('preview');
    }
  };

  // ── Derived counts ──────────────────────────────────────────────────────────

  const matchedCount   = matchRows.filter((r) => r.matchedPlayerId !== null).length;
  const unmatchedCount = matchRows.length - matchedCount;
  const quarterCounts  = plays.reduce<Record<number, number>>((acc, p) => {
    if (p.quarter) acc[p.quarter] = (acc[p.quarter] ?? 0) + 1;
    return acc;
  }, {});

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hudl-modal-title"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-w-2xl w-full flex flex-col"
        style={{ maxHeight: 'calc(100vh - 60px)' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 id="hudl-modal-title" className="text-lg font-semibold text-gray-900">
            Import Hudl Play-by-Play
          </h2>
          {step !== 'importing' && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-4">

          {/* ── Step: file select ── */}
          {step === 'select' && (
            <>
              {hasExistingStats && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <p>This game already has stats. Importing will replace them with the Hudl data.</p>
                </div>
              )}

              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                role="button"
                aria-label="Choose Hudl CSV file"
              >
                <Upload size={36} className="text-gray-400" />
                <div className="text-center">
                  <p className="font-medium text-gray-700">Drop your Hudl CSV here</p>
                  <p className="text-sm text-gray-500 mt-0.5">or click to browse</p>
                </div>
                <p className="text-xs text-gray-400">Export from Hudl → Reports → Play-by-Play</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileInput}
                aria-label="Hudl CSV file input"
              />
            </>
          )}

          {/* ── Step: preview ── */}
          {step === 'preview' && (
            <>
              {/* Summary card */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600 shrink-0" />
                  <span className="font-medium text-gray-900 text-sm">{fileName}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{plays.length}</p>
                    <p className="text-xs text-gray-500">Plays</p>
                  </div>
                  {Object.entries(quarterCounts).sort(([a],[b]) => +a - +b).map(([q, n]) => (
                    <div key={q} className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{n}</p>
                      <p className="text-xs text-gray-500">Q{q}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-sm pt-1 border-t border-gray-200">
                  <span className="flex items-center gap-1 text-green-700">
                    <CheckCircle size={13} /> {matchedCount} matched
                  </span>
                  {unmatchedCount > 0 && (
                    <span className="flex items-center gap-1 text-amber-700">
                      <AlertTriangle size={13} /> {unmatchedCount} unmatched
                    </span>
                  )}
                </div>
              </div>

              {/* Parse warnings */}
              {parseWarnings.length > 0 && (
                <div className="space-y-1">
                  {parseWarnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                      {w}
                    </div>
                  ))}
                </div>
              )}

              {/* Player matching table */}
              {matchRows.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Player Matching</h3>
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-3 py-2 text-left">Hudl Name</th>
                          <th className="px-3 py-2 text-left hidden sm:table-cell">#</th>
                          <th className="px-3 py-2 text-left">Matched Roster Player</th>
                          <th className="px-3 py-2 text-center w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {matchRows.map((row) => (
                          <tr key={row.ref.raw} className={row.matchedPlayerId ? '' : 'bg-amber-50/50'}>
                            <td className="px-3 py-2 font-medium text-gray-800 truncate max-w-[140px]">
                              {row.ref.name || row.ref.raw}
                            </td>
                            <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">
                              {row.ref.jerseyNumber ? `#${row.ref.jerseyNumber}` : '—'}
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={row.matchedPlayerId ?? ''}
                                onChange={(e) => setMatch(row.ref.raw, e.target.value || null)}
                                className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary focus:border-transparent"
                                aria-label={`Match player ${row.ref.name}`}
                              >
                                <option value="">— unmatched —</option>
                                {players
                                  .sort((a, b) => (a.number ?? 999) - (b.number ?? 999))
                                  .map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {playerLabel(p)}
                                    </option>
                                  ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.matchedPlayerId
                                ? <CheckCircle size={16} className="text-green-500 mx-auto" />
                                : <XCircle    size={16} className="text-amber-400 mx-auto" />
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {unmatchedCount > 0 && (
                    <p className="text-xs text-gray-500 mt-1.5">
                      Unmatched players will be skipped when calculating stats — assign them above if needed.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Step: importing ── */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
              <p className="text-sm text-gray-600">{importStatus || 'Importing…'}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'importing' && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 flex gap-3">
            {step === 'select' && (
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
                Cancel
              </button>
            )}
            {step === 'preview' && (
              <>
                <button
                  type="button"
                  onClick={() => { setStep('select'); setPlays([]); setMatchRows([]); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                >
                  Import {plays.length} Plays
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
